import type { Message } from '@vav/client-core';

/**
 * A notification for a message that arrived while the app was not being looked
 * at.
 *
 * Raised here rather than by the service worker, and that distinction is the
 * whole point. The push the server sends is a doorbell with nothing in it — the
 * server has never seen the plaintext and never will — so the worker can only
 * say "new message". This runs after the client has decrypted, so it can say
 * who it is from, and nothing about who is talking to whom leaves the device to
 * make that possible.
 *
 * Only when the page is hidden. A notification for a conversation the user is
 * looking at is noise about something already on screen.
 */
export function notifyIncoming(message: Message, senderName: string, preview: string): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible') return;

  const body = preview.slice(0, 140);

  void (async () => {
    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      const options: NotificationOptions = {
        body,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        // One entry per conversation: ten messages from the same person should
        // update one notification rather than stack ten identical rows.
        tag: `vav:${message.conversationId}`,
        renotify: true,
        data: { conversationId: message.conversationId },
      } as NotificationOptions;

      // An installed app has a worker and must use it — `new Notification()`
      // throws in that context on Android. A plain tab may have neither.
      if (registration) await registration.showNotification(senderName, options);
      else new Notification(senderName, options);
    } catch {
      // A notification failing is never worth interrupting message delivery.
    }
  })();
}

const CALL_TAG = 'vav-call';

/**
 * The system-level half of ringing: an incoming call has to wake the phone
 * and stand on the lock screen, not wait politely inside an app nobody is
 * looking at. `requireInteraction` keeps it up until answered or dismissed,
 * and tapping it lands in the app with the ring overlay already showing.
 * Raised only when the app is not front-and-centre — on screen, the overlay
 * itself is the ring.
 */
export function notifyCall(callerName: string, body: string, answerLabel: string, dismissLabel: string): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible' && document.hasFocus()) return;

  void (async () => {
    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      const options = {
        body,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: CALL_TAG,
        renotify: true,
        requireInteraction: true,
        vibrate: [600, 300, 600, 300, 600],
        // The worker's notificationclick handles both: answer carries intent,
        // dismiss just closes. Titles come from the active locale.
        actions: [
          { action: 'answer', title: `📞 ${answerLabel}` },
          { action: 'dismiss', title: dismissLabel },
        ],
        data: { url: '/' },
      } as NotificationOptions;
      // Actions only work through a worker registration; without one the
      // plain notification still opens the app on tap.
      if (registration) await registration.showNotification(callerName, options);
      else new Notification(callerName, { ...options, actions: undefined } as NotificationOptions);
    } catch {
      // The in-app overlay still rings.
    }
  })();
}

/** The ring stopped — answered, refused, or given up. Take the card down. */
export function closeCallNotification(): void {
  void (async () => {
    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      const open = await registration?.getNotifications({ tag: CALL_TAG });
      for (const notification of open ?? []) notification.close();
    } catch {
      // Nothing to close, or nowhere to ask.
    }
  })();
}
